import globalStore from "@/store/global.store";
import fridayReporter from "@/utils/friday";
import { debounce } from "@common/utils/common";
import {
  useState,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import RecordContent from "./RecordContent";
import SummaryContent from "./SummaryContent";
import summaryCommonStore from "@/store/summaryCommon.store";

const defaultPanelSize = 35;
interface IMainLayoutProps {
  hiddenSummary?: boolean;
}
export interface IMainLayoutRef {
  handleExpand: (val: boolean) => void;
}
const MainLayout = forwardRef<IMainLayoutRef, IMainLayoutProps>(
  ({ hiddenSummary = false }, ref) => {
    const [useTransition, setUseTransition] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const SummaryContentRef = useRef<ImperativePanelHandle>(null);
    const RecordContentRef = useRef<ImperativePanelHandle>(null);
    const lastPanelSize = useRef(defaultPanelSize);

    const isExpand = globalStore((state) => state.isSrcDocExpand);
    const isSummaryExpand = globalStore((state) => state.isSummaryExpand);
    const { setIsSrcDocExpand: setIsExpand, setIsSummaryExpand } =
      globalStore.getState();

    const simulateTransition = () => {
      setUseTransition(true);
      setTimeout(() => {
        setUseTransition(false);
      }, 300);
    };

    const handleExpand = (val: boolean) => {
      setIsExpand(val);
      if (val) {
        simulateTransition();
        lastPanelSize.current = 36;
        RecordContentRef.current?.resize(36);
      } else {
        simulateTransition();
        lastPanelSize.current = 0;
        RecordContentRef.current?.resize(0);
        SummaryContentRef.current?.expand(100);
      }
    };
    useImperativeHandle(ref, () => ({
      handleExpand,
    }));

    const debounceReportTextStretch = debounce((size) => {
      if (size !== lastPanelSize.current) {
        console.log("report ResizeRecordContent", size, lastPanelSize.current);
        lastPanelSize.current = size as number;
        fridayReporter.pushData({
          eventCode: "SummaryPage_Text_Stretch",
        });
      }
    }, 500);

    const handleResizeRecordContent = (size: number) => {
      debounceReportTextStretch(size);
      if (size < defaultPanelSize) {
        setIsExpand(false);
      } else {
        setIsExpand(true);
      }
    };

    const handleResizeSummaryContent = (size: number) => {
      if (size <= 40) {
        simulateTransition();
        SummaryContentRef.current?.resize(0);
        RecordContentRef.current?.resize(100);
        setIsSummaryExpand(false);
      } else {
        setIsSummaryExpand(true);
      }
    };

    const handleDragging = (isDrag: boolean) => {
      setIsDragging(isDrag);
    };

    const transitionStyle = useMemo(() => {
      return useTransition
        ? {
          transition: "all 0.3s",
        }
        : {};
    }, [useTransition]);

    const isLayoutFull = useMemo(() => {
      return (isExpand && !isSummaryExpand) || (!isExpand && isSummaryExpand);
    }, [isSummaryExpand, isExpand]);

    useEffect(() => {
      const abortController = new AbortController();
      summaryCommonStore.getState().setAbortPollingSignal(abortController);
      const abortRecordingPollingController = new AbortController();
      summaryCommonStore.getState().setAbortRecordingPollingSignal(abortRecordingPollingController);

      return () => {
        console.log('退出轮询回溯', abortController)
        summaryCommonStore.getState().abortPollingSignal?.abort();
        summaryCommonStore.getState().setAbortPollingSignal(null);
        summaryCommonStore.getState().abortRecordingPollingSignal?.abort();
        summaryCommonStore.getState().setAbortRecordingPollingSignal(null);
      };
    }, []);

    return (
      <PanelGroup direction="horizontal">
        {!hiddenSummary && (
          <Panel
            onResize={handleResizeSummaryContent}
            ref={SummaryContentRef}
            style={transitionStyle}
          >
            <SummaryContent />
          </Panel>
        )}
        {!hiddenSummary && (
          <PanelResizeHandle
            onDragging={handleDragging}
            className={`${isLayoutFull ? "hidden" : ""
              } relative left-2 z-1 w-.5 mt-2 mb-4 rounded-2.5 hover:bg-#4981F2 ${isDragging ? "bg-#4981F2" : ""
              }`}
          />
        )}
        <Panel
          defaultSize={hiddenSummary ? 100 : defaultPanelSize}
          minSize={hiddenSummary ? 100 : defaultPanelSize}
          collapsible={!hiddenSummary}
          onResize={handleResizeRecordContent}
          ref={RecordContentRef}
          style={transitionStyle}
        >
          <RecordContent isExpan={isExpand} isSummaryExpand={isSummaryExpand} isSummaryHidden={hiddenSummary} setIsExpan={handleExpand} />
        </Panel>
      </PanelGroup>
    );
  }
);

export default MainLayout;
